import { AvailabilitySlot, ResourceAvailabilitySlot } from '@/lib/types/availability'
import { normalizeDayOfWeek, serializeDayOfWeek } from '@/utils/date'
import api from './axios'

const baseUrl = '/rest/v1/resource-slots/{resourceId}/availability'

function mapAvailabilitySlotFromApi(slot: AvailabilitySlot): AvailabilitySlot {
    return {
        ...slot,
        dayOfWeek: normalizeDayOfWeek(slot.dayOfWeek)
    }
}

function mapAvailabilitySlotToApi(slot: AvailabilitySlot) {
    return {
        ...slot,
        dayOfWeek: serializeDayOfWeek(slot.dayOfWeek)
    }
}

export async function saveAvailabilitySlots(data: ResourceAvailabilitySlot): Promise<ErrorMessage | void> {
    try {
        // The endpoint answers 201 with an empty body on success — there is nothing to return.
        await api.post(
            baseUrl.replace('{resourceId}', data.resourceId),
            data.slots.map(mapAvailabilitySlotToApi)
        )
    } catch (error: any) {
        // Mirrors saveMaintenanceSlots: the save endpoint now lets OperationNotAllowedException reach
        // GlobalExceptionHandler, so a 400 carries the ErrorResponse the caller is declared to return.
        // Without this catch the axios rejection propagated and the store's `'errorCode' in res` branch
        // was unreachable, so the operator saw an unhandled rejection instead of the translated message.
        if (error?.response?.status === 400) {
            return {
                errorCode: error.response.data.errorCode,
                message: error.response.data.message
            }
        }

        throw error
    }
}

export async function getAvailabilitySlots(resourceId: string): Promise<ResourceAvailabilitySlot> {
    const res = await api.get(baseUrl.replace('{resourceId}', resourceId))
    const data: ResourceAvailabilitySlot = {
        resourceId: resourceId,
        slots: res.data.items.map(mapAvailabilitySlotFromApi)
    }
    return data
}

export async function checkAvailabilitySlotDeletable(availabilitySlotId: string): Promise<boolean> {
    try {
        await api.delete(baseUrl.replace('{resourceId}', availabilitySlotId))
        // 200 status code means slot is deletable
        return true
    } catch (error: any) {
        // 400 status code means slot has reservations and cannot be deleted
        if (error?.response?.status === 400) {
            return false
        }
        // For other errors, re-throw them
        throw error
    }
}

export async function deleteAvailabilitySlot(availabilitySlotId: string, reason?: string): Promise<void> {
    return deleteAvailabilitySlots([{ id: availabilitySlotId, reason }])
}

export async function deleteAvailabilitySlots(slotsToDelete: { id: string, reason?: string }[]): Promise<void> {
    const res = await api.delete(baseUrl.replace('/{resourceId}', ''), {
        data: slotsToDelete
    })
    return res.data
}
